// src/app/pages/user/ProfilePage.jsx
import { useEffect, useRef, useState } from "react";
import { FaCamera, FaCheck, FaEye, FaEyeSlash } from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import { ImCross } from "react-icons/im";
import { HiPhoto } from "react-icons/hi2";
import { TbPhotoDown } from "react-icons/tb";
import { IoIosCheckbox, IoIosWarning } from "react-icons/io";
import { RiInformationFill } from "react-icons/ri";
import { BiRefresh } from "react-icons/bi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useToast } from "../../components/ToastProvider";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const AVATAR_VIEW = 320;
const OUT_W = 150;
const OUT_H = 150;

const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#111111", amberBg: "rgba(0,0,0,0.06)", amberBorder: "rgba(0,0,0,0.12)",
  dark: "#0c1118", bg: "#f4f6f8", surface: "#ffffff", border: "#e8ecf0",
  muted: "#6b7280", mutedLight: "#9ca3af",
  green: "#16a34a", greenBg: "rgba(34,197,94,0.1)",
  red: "#dc2626", redBg: "rgba(239,68,68,0.1)", redBorder: "rgba(239,68,68,0.25)",
  blue: "#1d4ed8", blueBg: "rgba(59,130,246,0.1)",
};

if (!document.getElementById("adp-styles")) {
  const s = document.createElement("style");
  s.id = "adp-styles";
  s.textContent = `
    @keyframes adp-spin { to { transform: rotate(360deg); } }
    .adp-spin { animation: adp-spin 0.7s linear infinite; }
    .adp-input:focus { outline: none; border-color: #111111 !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
    .adp-btn:hover { opacity: 0.82; }
    .adp-dropzone:hover { border-color: #111111; background: rgba(0,0,0,0.02); }
  `;
  document.head.appendChild(s);
}

// ── helpers ───────────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: T.muted, fontFamily: T.fontBody, letterSpacing: "0.06em" }}>
        {label}{required && <span style={{ color: T.red, marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const INPUT_STYLE = { width: "100%", padding: "10px 14px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 14, fontFamily: T.fontBody, background: T.surface, color: T.dark, boxSizing: "border-box" };

function MsgAlert({ message, onClose }) {
  const cfg = {
    success: { bg: T.greenBg, border: "rgba(34,197,94,0.25)", color: T.green, Icon: IoIosCheckbox },
    error:   { bg: T.redBg,   border: T.redBorder,             color: T.red,   Icon: ImCross },
    info:    { bg: T.amberBg, border: T.amberBorder,           color: "#333333", Icon: RiInformationFill },
  }[message.type] || { bg: T.amberBg, border: T.amberBorder, color: "#333333", Icon: RiInformationFill };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, padding: "12px 16px", background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <cfg.Icon color={cfg.color} size={18} style={{ marginTop: 1, flexShrink: 0 }} />
        <p style={{ fontSize: 13, color: cfg.color, margin: 0 }}>{message.text}</p>
      </div>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: cfg.color, fontSize: 16, lineHeight: 1 }}>×</button>
    </div>
  );
}

function PwStrengthBar({ password }) {
  if (!password) return null;
  const checks = {
    length: password.length >= 8, lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password), numbers: /\d/.test(password),
    symbols: /[^a-zA-Z0-9]/.test(password), longLength: password.length >= 12,
  };
  const score = Object.values(checks).filter(Boolean).length;
  const { label, color } =
    score <= 2 ? { label: "Weak",   color: T.red } :
    score <= 4 ? { label: "Fair",   color: "#f59e0b" } :
    score <= 5 ? { label: "Good",   color: T.blue } :
                 { label: "Strong", color: T.green };
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: T.muted }}>Password strength:</span>
        <span style={{ fontWeight: 700, color }}>{label}</span>
      </div>
      <div style={{ height: 4, background: T.border, borderRadius: 99 }}>
        <div style={{ height: "100%", width: `${(score / 6) * 100}%`, background: color, borderRadius: 99, transition: "width 0.3s" }} />
      </div>
      <p style={{ fontSize: 11, color: T.mutedLight, marginTop: 4 }}>8+ chars, uppercase, lowercase, numbers, symbols</p>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function AdminProfilePage() {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ firstname: "", lastname: "", country: "", birthday: "", phone: "", phoneCountryCode: "", email: "" });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [msg, setMsg] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  const toast = useToast();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${backendBase}/api/profile/me`, { credentials: "include", cache: "no-store" });
        if (r.ok) {
          const d = await r.json();
          setForm({ firstname: d.user.firstname || "", lastname: d.user.lastname || "", country: d.user.country || "", birthday: d.user.birthday ? String(d.user.birthday).slice(0, 10) : "", phone: d.user.phone || "", phoneCountryCode: "", email: d.user.email || "" });
          setAvatarUrl(d.user.avatarUrl || null);
          setPendingEmail(d.user.pendingEmail || null);
        }
      } finally { setLoading(false); }
    })();
  }, [backendBase]);

  const handlePhoneChange = (value, country) => setForm((p) => ({ ...p, phone: value, phoneCountryCode: country.countryCode || "" }));

  async function save() {
    setMsg(null); setSaving(true);
    try {
      const r = await fetch(`${backendBase}/api/profile/me`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, phonenumber: form.phone, phoneIso2: form.phoneCountryCode.toUpperCase() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg({ type: "error", text: d?.message || "Save failed" }); return; }
      setMsg({ type: "success", text: "Profile updated successfully!" });
      window.dispatchEvent(new CustomEvent("profile:updated", { detail: { firstname: d.user?.firstname, lastname: d.user?.lastname, email: d.user?.email } }));
      setPendingEmail(d.user?.pendingEmail || null);
      if (d.requiresEmailVerify) setMsg({ type: "info", text: "We sent a verification code to your new email address. Please check your inbox to complete the change." });
    } finally { setSaving(false); }
  }

  async function confirmEmailChange() {
    const code = prompt("Enter the 6-digit code sent to your new email:");
    if (!code) return;
    try {
      const r = await fetch(`${backendBase}/api/profile/confirm-email`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) });
      if (r.ok) { setMsg({ type: "success", text: "Your email has been updated and verified successfully!" }); setPendingEmail(null); }
      else { const d = await r.json().catch(() => ({})); toast.error(d?.message || "Verification failed"); }
    } catch { toast.error("Verification failed. Please try again."); }
  }

  async function resendEmailCode() {
    try {
      const r = await fetch(`${backendBase}/api/profile/resend-email-code`, { method: "POST", credentials: "include" });
      if (r.ok) setMsg({ type: "success", text: "Verification code resent to your email." });
    } catch { setMsg({ type: "error", text: "Failed to resend code. Please try again." }); }
  }

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=";
    let p = "aA1!";
    for (let i = 4; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPw((prev) => ({ ...prev, next: p.split("").sort(() => Math.random() - 0.5).join("") }));
  };

  async function changePassword() {
    setPwMsg(null);
    if (pw.next !== pw.confirm) { setPwMsg({ type: "error", text: "New passwords do not match" }); return; }
    if (pw.next.length < 8) { setPwMsg({ type: "error", text: "Password must be at least 8 characters long" }); return; }
    try {
      const r = await fetch(`${backendBase}/api/profile/change-password`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setPwMsg({ type: "error", text: d?.message || "Password change failed" });
      else { setPwMsg({ type: "success", text: "Password updated successfully!" }); setPw({ current: "", next: "", confirm: "" }); setShowPw({ current: false, next: false, confirm: false }); }
    } catch { setPwMsg({ type: "error", text: "Password change failed. Please try again." }); }
  }

  const togglePw = (f) => setShowPw((p) => ({ ...p, [f]: !p[f] }));

  const canSavePw = pw.current && pw.next && pw.confirm && pw.next === pw.confirm;

  // ── loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg, fontFamily: T.fontBody }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.muted }}>
          <div className="adp-spin" style={{ width: 22, height: 22, border: `2px solid ${T.border}`, borderTopColor: "#111111", borderRadius: "50%" }} />
          Loading your profile…
        </div>
      </div>
    );
  }

  const CARD = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 24, marginBottom: 0 };
  const SECTION_HEAD = { fontFamily: T.fontBody, fontSize: 16, fontWeight: 600, color: T.dark, margin: 0 };

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.fontBody }}>
      <div>

        {/* Header */}
        <div style={{ marginBottom: isMobile ? 20 : 28 }}>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>Profile Settings</h1>
          {!isMobile && <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Manage your personal information and account preferences</p>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 24, alignItems: "start" }}>
          {/* Avatar column */}
          <AvatarPickerCard currentUrl={avatarUrl} backendBase={backendBase} onSaved={(url) => setAvatarUrl(url)} onRemoved={() => setAvatarUrl(null)} />

          {/* Right column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Personal Info */}
            <div style={CARD}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h2 style={SECTION_HEAD}>Personal Information</h2>
                  {!isMobile && <p style={{ color: T.muted, fontSize: 13, margin: "4px 0 0" }}>Update your basic profile details</p>}
                </div>
                {!isMobile && (
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: 11, color: T.mutedLight, margin: 0 }}>Profile Status</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <span style={{ width: 8, height: 8, background: T.green, borderRadius: "50%", display: "inline-block" }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.green }}>Complete</span>
                    </div>
                  </div>
                )}
              </div>

              {msg && <MsgAlert message={msg} onClose={() => setMsg(null)} />}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18 }}>
                <Field label="First Name" required>
                  <input className="adp-input" style={INPUT_STYLE} placeholder="Enter your first name" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} />
                </Field>
                <Field label="Last Name" required>
                  <input className="adp-input" style={INPUT_STYLE} placeholder="Enter your last name" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} />
                </Field>
                <Field label="Email Address" required>
                  <input className="adp-input" style={INPUT_STYLE} type="email" placeholder="Enter your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Field>
                <Field label="Phone Number">
                  <PhoneInput
                    country={"us"} value={form.phone} onChange={handlePhoneChange}
                    inputProps={{ name: "phone", required: false }}
                    enableSearch disableSearchIcon autoFormat specialLabel=""
                    containerStyle={{ width: "100%" }}
                    inputStyle={{ width: "100%", height: 44, fontSize: 14, paddingLeft: 60, border: `1px solid ${T.border}`, borderRadius: 8, backgroundColor: T.surface }}
                    buttonStyle={{ border: `1px solid ${T.border}`, borderRadius: "8px 0 0 8px", backgroundColor: T.surface }}
                    dropdownStyle={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
                    searchStyle={{ padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 8, margin: 8 }}
                  />
                </Field>
                <Field label="Country">
                  <input className="adp-input" style={INPUT_STYLE} placeholder="Enter your country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </Field>
                <Field label="Date of Birth">
                  <input className="adp-input" style={INPUT_STYLE} type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
                </Field>
              </div>

              {/* Pending email banner */}
              {pendingEmail && (
                <div style={{ marginTop: 20, padding: "14px 16px", background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <IoIosWarning size={20} color="#333333" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 700, color: "#111111", margin: "0 0 4px", fontSize: 13 }}>Email Verification Required</p>
                      <p style={{ color: "#333333", fontSize: 13, margin: "0 0 10px" }}>
                        We've sent a verification code to <strong>{pendingEmail}</strong>. Please verify your new email address to complete the change.
                      </p>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="adp-btn" onClick={confirmEmailChange} style={{ padding: "7px 14px", background: "#111111", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Enter Verification Code</button>
                        <button className="adp-btn" onClick={resendEmailCode} style={{ padding: "7px 14px", background: "transparent", color: "#111111", border: `1px solid rgba(0,0,0,0.12)`, borderRadius: 6, fontSize: 13, cursor: "pointer" }}>Resend Code</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button className="adp-btn" onClick={save} disabled={saving}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: T.dark, color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                  {saving && <div className="adp-spin" style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%" }} />}
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>

            {/* Security / Password */}
            <div style={CARD}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <h2 style={SECTION_HEAD}>Security Settings</h2>
                  {!isMobile && <p style={{ color: T.muted, fontSize: 13, margin: "4px 0 0" }}>Update your password and security preferences</p>}
                </div>
                {!isMobile && (
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: 11, color: T.mutedLight, margin: 0 }}>Password Strength</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <span style={{ width: 8, height: 8, background: T.green, borderRadius: "50%", display: "inline-block" }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.green }}>Strong</span>
                    </div>
                  </div>
                )}
              </div>

              {pwMsg && <MsgAlert message={pwMsg} onClose={() => setPwMsg(null)} />}

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 18 }}>
                {/* Current password */}
                <Field label="Current Password" required>
                  <div style={{ position: "relative" }}>
                    <input className="adp-input" style={{ ...INPUT_STYLE, paddingRight: 40 }} type={showPw.current ? "text" : "password"} placeholder="Enter current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
                    <button type="button" onClick={() => togglePw("current")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.muted }}>
                      {showPw.current ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                </Field>

                {/* New password */}
                <Field label="New Password" required>
                  <div style={{ position: "relative" }}>
                    <input className="adp-input" style={{ ...INPUT_STYLE, paddingRight: 64 }} type={showPw.next ? "text" : "password"} placeholder="Minimum 8 characters" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
                    <div style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 4 }}>
                      <button type="button" onClick={generatePassword} title="Generate secure password" style={{ background: "none", border: "none", cursor: "pointer", color: "#111111", padding: 2 }}><BiRefresh size={16} /></button>
                      <button type="button" onClick={() => togglePw("next")} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: 2 }}>
                        {showPw.next ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </div>
                  <PwStrengthBar password={pw.next} />
                </Field>

                {/* Confirm password */}
                <Field label="Confirm New Password" required>
                  <div style={{ position: "relative" }}>
                    <input className="adp-input" style={{ ...INPUT_STYLE, paddingRight: 40 }} type={showPw.confirm ? "text" : "password"} placeholder="Repeat new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
                    <button type="button" onClick={() => togglePw("confirm")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.muted }}>
                      {showPw.confirm ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                    </button>
                  </div>
                  {pw.next && pw.confirm && pw.next !== pw.confirm && (
                    <p style={{ fontSize: 11, color: T.red, margin: "4px 0 0" }}>Passwords do not match</p>
                  )}
                </Field>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button className="adp-btn" onClick={changePassword} disabled={!canSavePw}
                  style={{ padding: "10px 24px", background: T.surface, color: T.dark, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: canSavePw ? "pointer" : "not-allowed", opacity: canSavePw ? 1 : 0.4 }}>
                  Update Password
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ── Avatar picker ─────────────────────────────────────────────────────────────
function AvatarPickerCard({ currentUrl, backendBase, onSaved, onRemoved }) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState("idle");
  const [sourceUrl, setSourceUrl] = useState(null);
  const imgEl = useRef(null);
  const [disp, setDisp] = useState({ w: 0, h: 0, scale: 1, offsetX: 0, offsetY: 0 });
  const [box, setBox] = useState({ x: 0, y: 0, s: 0 });
  const dragRef = useRef({ active: false, ox: 0, oy: 0, sx: 0, sy: 0 });
  const resizeRef = useRef({ active: false, corner: null, ox: 0, oy: 0, s: 0, x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function onPick(file) {
    if (!file) return;
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(URL.createObjectURL(file));
    setMode("editing"); setErr("");
  }
  function onDrop(e) { e.preventDefault(); onPick(e.dataTransfer?.files?.[0] || null); }
  function onDragOver(e) { e.preventDefault(); }
  function cancel() { if (sourceUrl) URL.revokeObjectURL(sourceUrl); setSourceUrl(null); setMode("idle"); setErr(""); }

  function onImgLoad(e) {
    const img = e.target;
    const scale = Math.min(AVATAR_VIEW / img.naturalWidth, AVATAR_VIEW / img.naturalHeight);
    const dw = Math.round(img.naturalWidth * scale);
    const dh = Math.round(img.naturalHeight * scale);
    const offsetX = Math.floor((AVATAR_VIEW - dw) / 2);
    const offsetY = Math.floor((AVATAR_VIEW - dh) / 2);
    setDisp({ w: dw, h: dh, scale, offsetX, offsetY });
    const s = Math.floor(Math.min(dw, dh) * 0.9);
    setBox({ x: offsetX + Math.floor((dw - s) / 2), y: offsetY + Math.floor((dh - s) / 2), s });
  }

  function beginDrag(e) {
    e.preventDefault();
    dragRef.current.active = true;
    dragRef.current.ox = "touches" in e ? e.touches[0].clientX : e.clientX;
    dragRef.current.oy = "touches" in e ? e.touches[0].clientY : e.clientY;
    dragRef.current.sx = box.x; dragRef.current.sy = box.y;
  }
  function beginResize(e, corner) {
    e.preventDefault();
    resizeRef.current = { active: true, corner, ox: "touches" in e ? e.touches[0].clientX : e.clientX, oy: "touches" in e ? e.touches[0].clientY : e.clientY, s: box.s, x: box.x, y: box.y };
  }

  useEffect(() => {
    function onMove(e) {
      const mx = "touches" in e ? e.touches[0].clientX : e.clientX;
      const my = "touches" in e ? e.touches[0].clientY : e.clientY;
      if (resizeRef.current.active) {
        const dx = mx - resizeRef.current.ox; const dy = my - resizeRef.current.oy;
        const delta = Math.max(dx, dy);
        let newS = resizeRef.current.s, newX = resizeRef.current.x, newY = resizeRef.current.y;
        switch (resizeRef.current.corner) {
          case "tl": newS = Math.max(20, resizeRef.current.s - delta); newX = resizeRef.current.x + (resizeRef.current.s - newS); newY = resizeRef.current.y + (resizeRef.current.s - newS); break;
          case "tr": newS = Math.max(20, resizeRef.current.s + dx); newY = resizeRef.current.y + (resizeRef.current.s - newS); break;
          case "bl": newS = Math.max(20, resizeRef.current.s + dy); newX = resizeRef.current.x + (resizeRef.current.s - newS); break;
          case "br": newS = Math.max(20, resizeRef.current.s + delta); break;
          default: break;
        }
        const maxS = Math.min(disp.w, disp.h);
        newS = Math.min(newS, maxS);
        newX = Math.max(disp.offsetX, Math.min(disp.offsetX + disp.w - newS, newX));
        newY = Math.max(disp.offsetY, Math.min(disp.offsetY + disp.h - newS, newY));
        setBox({ x: newX, y: newY, s: newS }); return;
      }
      if (!dragRef.current.active) return;
      const nx = dragRef.current.sx + (mx - dragRef.current.ox);
      const ny = dragRef.current.sy + (my - dragRef.current.oy);
      setBox({ ...box, x: Math.max(disp.offsetX, Math.min(disp.offsetX + disp.w - box.s, nx)), y: Math.max(disp.offsetY, Math.min(disp.offsetY + disp.h - box.s, ny)) });
    }
    function onUp() { dragRef.current.active = false; resizeRef.current.active = false; }
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false }); window.addEventListener("touchend", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onUp); };
  }, [box, disp]); // eslint-disable-line

  async function confirm() {
    const img = imgEl.current; if (!img) return;
    setBusy(true); setErr("");
    try {
      const sx = Math.round((box.x - disp.offsetX) / disp.scale);
      const sy = Math.round((box.y - disp.offsetY) / disp.scale);
      const ss = Math.round(box.s / disp.scale);
      const cnv = document.createElement("canvas"); cnv.width = OUT_W; cnv.height = OUT_H;
      const ctx = cnv.getContext("2d"); ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, ss, ss, 0, 0, OUT_W, OUT_H);
      const blob = await new Promise((res) => cnv.toBlob(res, "image/png", 0.92));
      const fd = new FormData(); fd.append("file", blob, "avatar.png");
      const r = await fetch(`${backendBase}/api/profile/avatar`, { method: "POST", credentials: "include", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || "Upload failed");
      onSaved?.(d.avatarUrl);
      window.dispatchEvent(new CustomEvent("avatar:updated", { detail: d.avatarUrl }));
      cancel();
    } catch (e) { setErr(e.message || "Upload failed"); }
    finally { setBusy(false); }
  }

  async function removeAvatar() {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${backendBase}/api/profile/avatar`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Remove failed");
      onRemoved?.();
      window.dispatchEvent(new CustomEvent("avatar:updated", { detail: null }));
    } catch (e) { setErr(e.message || "Remove failed"); }
    finally { setBusy(false); }
  }

  const CARD = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 };

  return (
    <div style={CARD}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ fontFamily: T.fontBody, fontSize: 16, fontWeight: 600, color: T.dark, margin: 0 }}>Profile Picture</h2>
          {!isMobile && <p style={{ color: T.muted, fontSize: 13, margin: "4px 0 0" }}>Upload a square image for best results</p>}
        </div>
        {!isMobile && (
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 11, color: T.mutedLight, margin: 0 }}>Image Quality</p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span style={{ width: 8, height: 8, background: T.green, borderRadius: "50%", display: "inline-block" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: T.green }}>HD Ready</span>
            </div>
          </div>
        )}
      </div>

      {mode === "idle" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Current avatar */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <img src={currentUrl || "/assets/profile.png"} alt="Current avatar" style={{ width: 80, height: 80, borderRadius: "50%", border: `3px solid ${T.border}`, objectFit: "cover" }} />
              {currentUrl && (
                <div style={{ position: "absolute", top: -4, right: -4, width: 22, height: 22, background: T.green, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaCheck size={10} color="#fff" />
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 600, color: T.dark, margin: "0 0 4px" }}>{currentUrl ? "Current Profile Picture" : "No Profile Picture"}</p>
              <p style={{ fontSize: 13, color: T.muted, margin: "0 0 10px" }}>{currentUrl ? "Your profile picture is displayed across the platform" : "Upload a profile picture to personalize your account"}</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="adp-btn" onClick={() => document.getElementById("avatar-file-input")?.click()}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", background: T.dark, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <FaCamera size={12} /> {currentUrl ? "Change" : "Upload"}
                </button>
                {currentUrl && (
                  <button className="adp-btn" onClick={removeAvatar} disabled={busy}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", background: T.surface, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}>
                    <MdDelete size={13} /> {busy ? "Removing…" : "Remove"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Drop zone */}
          <IdleDropZone onPick={onPick} />

          {err && (
            <div style={{ padding: "10px 14px", background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.red }}>
              <ImCross size={12} /> {err}
            </div>
          )}
        </div>
      )}

      {mode === "editing" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ textAlign: "center" }}>
            <p style={{ fontWeight: 600, color: T.dark, margin: "0 0 4px" }}>Crop Your Picture</p>
            <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>Drag the selection area to choose which part to use</p>
          </div>

          {/* Editor */}
          <div style={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
            <div style={{ border: `2px solid ${T.border}`, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
              <div style={{ position: "relative", width: AVATAR_VIEW, height: AVATAR_VIEW, background: T.bg }} onDrop={onDrop} onDragOver={onDragOver}>
                <img ref={imgEl} src={sourceUrl} alt="Crop" onLoad={onImgLoad} draggable={false}
                  style={{ position: "absolute", left: disp.offsetX, top: disp.offsetY, width: disp.w || "auto", height: disp.h || "auto", userSelect: "none" }} />
                {/* Crop box */}
                <div onMouseDown={beginDrag} onTouchStart={beginDrag}
                  style={{ position: "absolute", left: box.x, top: box.y, width: box.s, height: box.s, border: `2px solid #111111`, cursor: "move", background: "rgba(0,0,0,0.10)", boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} />
                {/* Resize handles */}
                {[{ c: "tl", l: box.x - 5, t: box.y - 5, cur: "nw-resize" }, { c: "tr", l: box.x + box.s - 5, t: box.y - 5, cur: "ne-resize" }, { c: "bl", l: box.x - 5, t: box.y + box.s - 5, cur: "sw-resize" }, { c: "br", l: box.x + box.s - 5, t: box.y + box.s - 5, cur: "se-resize" }].map(({ c, l, t, cur }) => (
                  <div key={c} onMouseDown={(e) => beginResize(e, c)} onTouchStart={(e) => beginResize(e, c)}
                    style={{ position: "absolute", left: l, top: t, width: 10, height: 10, background: "#111111", border: "2px solid #fff", borderRadius: "50%", cursor: cur }} />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: T.muted, margin: "0 0 8px", fontFamily: T.fontBody, letterSpacing: "0.06em" }}>Preview</p>
            <div style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", border: `3px solid ${T.border}`, margin: "0 auto", backgroundImage: `url(${sourceUrl})`, backgroundPosition: `${-((box.x - disp.offsetX) / disp.scale) * (72 / (box.s / disp.scale))}px ${-((box.y - disp.offsetY) / disp.scale) * (72 / (box.s / disp.scale))}px`, backgroundSize: `${(disp.w / disp.scale) * (72 / (box.s / disp.scale))}px ${(disp.h / disp.scale) * (72 / (box.s / disp.scale))}px` }} />
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
            <button className="adp-btn" onClick={cancel} disabled={busy}
              style={{ padding: "9px 20px", border: `1px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.muted, fontSize: 13, cursor: "pointer", opacity: busy ? 0.5 : 1 }}>Cancel</button>
            <button className="adp-btn" onClick={confirm} disabled={busy}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 20px", background: T.dark, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
              {busy && <div className="adp-spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%" }} />}
              {busy ? "Saving…" : "Save Picture"}
            </button>
          </div>

          {err && (
            <div style={{ padding: "10px 14px", background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.red }}>
              <ImCross size={12} /> {err}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Drop zone ─────────────────────────────────────────────────────────────────
function IdleDropZone({ onPick }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer.files?.[0]) onPick(e.dataTransfer.files[0]);
  };

  return (
    <div
      className="adp-dropzone"
      onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
      style={{ border: `2px dashed ${dragActive ? "#111111" : T.border}`, borderRadius: 8, padding: 24, textAlign: "center", background: dragActive ? "rgba(0,0,0,0.04)" : "transparent", transition: "all 0.2s" }}
    >
      <div style={{ fontSize: 32, marginBottom: 10, color: T.muted }}>{dragActive ? <TbPhotoDown /> : <HiPhoto />}</div>
      <p style={{ fontWeight: 600, color: T.dark, margin: "0 0 4px" }}>{dragActive ? "Drop your image here" : "Drag & drop your image"}</p>
      <p style={{ fontSize: 12, color: T.mutedLight, margin: "0 0 14px" }}>Supports PNG, JPG, GIF up to 2MB</p>
      <button className="adp-btn" onClick={() => inputRef.current?.click()}
        style={{ padding: "8px 20px", background: T.dark, color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        Browse Files
      </button>
      <input id="avatar-file-input" ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
}
